import React from 'react';
import { Box, Button, Flex, Text } from 'src/components/ui';
import { MapContainer, TileLayer, Marker, Circle } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

interface Address {
	street?: string;
	city?: string;
	state?: string;
	postalCode?: string;
	country?: string;
	lat?: number | string;
	long?: number | string;
}

interface LocationCardProps {
	title: string;
	address: Address;
	type: 'location' | 'service-area';
	radius?: number;
	entityType?: 'neighborhood' | 'city' | 'county' | 'state' | 'country';
	onEdit?: () => void;
	onRemove?: () => void;
	onClick?: () => void;
	isSelected?: boolean;
	height?: string;
	maxWidth?: string;
}

const LocationCard: React.FC<LocationCardProps> = ({
	title,
	address,
	type,
	radius,
	entityType,
	onEdit,
	onRemove,
	onClick,
	isSelected = false,
	height = '200px',
	maxWidth = '100%',
}) => {
	const hasCoordinates =
		address.lat &&
		address.long &&
		!isNaN(Number(address.lat)) &&
		!isNaN(Number(address.long)) &&
		address.lat.toString().trim() !== '' &&
		address.long.toString().trim() !== '';

	// Get appropriate zoom level based on entity type or default
	const getMapZoom = () => {
		if (entityType) {
			switch (entityType) {
				case 'neighborhood': return 13;
				case 'city': return 11;
				case 'county': return 9;
				case 'state': return 6;
				case 'country': return 4;
				default: return 11;
			}
		}
		return type === 'service-area' ? 13 : 15;
	};
	const mapZoom = getMapZoom();

	const formatAddress = () => {
		const mainAddress = [address.street, address.city, address.state]
			.filter(Boolean)
			.join(', ');

		const secondaryAddress = [address.postalCode, address.country]
			.filter(Boolean)
			.join(', ');

		return { mainAddress, secondaryAddress };
	};

	const { mainAddress, secondaryAddress } = formatAddress();

	const handleClick = () => {
		if (onClick) {
			onClick();
		}
	};

	return (
		<Box
			position="relative"
			borderRadius="xl"
			overflow="hidden"
			h={height}
			maxW={maxWidth}
			bg="gray.100"
			boxShadow="lg"
			border={isSelected ? '3px solid #F7480B' : '3px solid transparent'}
			cursor={onClick ? 'pointer' : 'default'}
			transition="all 0.2s ease-in-out"
			_hover={onClick ? {
				transform: 'translateY(-2px)',
				boxShadow: 'xl',
			} : {}}
			onClick={handleClick}
		>
			{/* Map Background */}
			{hasCoordinates && (
				<Box position="absolute" inset="0" zIndex={0}>
					<MapContainer
						center={[Number(address.lat), Number(address.long)]}
						zoom={mapZoom}
						style={{
							width: '100%',
							height: '100%',
						}}
						scrollWheelZoom={false}
						dragging={false}
						doubleClickZoom={false}
						zoomControl={false}
						attributionControl={false}
					>
						<TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
						<Marker
							position={[Number(address.lat), Number(address.long)]}
							icon={L.icon({
								iconUrl:
									'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
								iconSize: [25, 41],
								iconAnchor: [12, 41],
							})}
						/>
						{type === 'service-area' && radius && (
							<Circle
								center={[Number(address.lat), Number(address.long)]}
								radius={radius * 1000}
								pathOptions={{
									color: '#2C6ECB',
									fillOpacity: 0.2,
									weight: 1,
								}}
							/>
						)}
					</MapContainer>
				</Box>
			)}

			{/* Overlay Content */}
			<Box
				position="absolute"
				inset="0"
				zIndex={1}
				bg="rgba(0, 0, 0, 0.4)"
				display="flex"
				flexDirection="column"
				justifyContent="space-between"
				p={4}
			>
				{/* Header with controls */}
				<Flex justifyContent="space-between" alignItems="flex-start" gap={2}>
					<Box flex={1} minW={0}>
						<Text
							color="white"
							fontSize="lg"
							fontWeight="bold"
							textShadow="0 1px 3px rgba(0,0,0,0.5)"
							noOfLines={2}
						>
							{title}
						</Text>
						{type === 'service-area' && (radius || entityType) && (
							<Text
								color="rgba(255,255,255,0.9)"
								fontSize="sm"
								fontWeight="medium"
								textShadow="0 1px 3px rgba(0,0,0,0.5)"
								mt={1}
							>
								{radius ? `Radius: ${radius} mi` : entityType ? `Type: ${entityType.charAt(0).toUpperCase() + entityType.slice(1)}` : ''}
							</Text>
						)}
					</Box>

					<Flex gap={2} alignItems="flex-start" flexShrink={0}>
						{onEdit && (
							<Button
								size="sm"
								bg="rgba(255,255,255,0.2)"
								backdropFilter="blur(10px)"
								border="1px solid rgba(255,255,255,0.3)"
								color="white"
								_hover={{
									bg: 'rgba(255,255,255,0.3)',
								}}
								onClick={(e) => {
									e.stopPropagation();
									onEdit();
								}}
							>
								Edit
							</Button>
						)}
						{onRemove && (
							<Button
								size="sm"
								bg="rgba(220,38,38,0.8)"
								backdropFilter="blur(10px)"
								border="1px solid rgba(255,255,255,0.3)"
								color="white"
								_hover={{
									bg: 'rgba(220,38,38,0.9)',
								}}
								onClick={(e) => {
									e.stopPropagation();
									onRemove();
								}}
							>
								Delete
							</Button>
						)}
					</Flex>
				</Flex>

				{/* Address at bottom */}
				<Box>
					{mainAddress && (
						<Text
							color="white"
							fontSize="lg"
							fontWeight="bold"
							textShadow="0 1px 3px rgba(0,0,0,0.5)"
							mb={1}
						>
							{mainAddress}
						</Text>
					)}
					{secondaryAddress && (
						<Text
							color="rgba(255,255,255,0.8)"
							fontSize="sm"
							textShadow="0 1px 3px rgba(0,0,0,0.5)"
						>
							{secondaryAddress}
						</Text>
					)}
				</Box>
			</Box>
		</Box>
	);
};

export default LocationCard;
